/**
 * ManagerDashboard.js
 *
 * Location-scoped oversight dashboard for Manager role
 *
 * Shows 9 dashboard sections:
 * 1. Statistics Overview - Quick health snapshot
 * 2. Financial Summary - Budget and asset tracking (FULLY VISIBLE)
 * 3. Alert Summary - Critical items requiring attention
 * 4. Team Workload & Performance - Coverage, fair share, and pace
 * 5. Complaints Overview - Complaint pipeline status
 * 6. Maintenance Schedule - Device maintenance due dates
 * 7. Device Status - Portfolio health
 * 8. Cost Analysis - Budget planning and cost breakdown
 * 9. Recent Activity - System change log
 *
 * Scope: Location-scoped (manager's assigned location, all departments within location)
 * Financial data: FULLY VISIBLE for budget planning and oversight
 *
 */

import React, { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import BaseDashboard from './BaseDashboard';
import DashboardLayout, { Section } from './DashboardLayout';
import EmptyState from '../widgets/Shared/EmptyState';
import { DeviceIDBadge, ComplaintIDBadge, StatusBadge } from '../../../shared/components/common/Badges';
import ComplaintView from '../../complaints/components/ComplaintView';
import MaintenanceLogView from '../../devices/components/MaintenanceLogView';
import CalendarView from '../../devices/components/CalendarView';
import ApprovalsTableWidget from '../widgets/Lists/ApprovalsTableWidget';
import { formatCurrencyDynamic, formatPercentage } from '../../../utils/numberUtils';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { ComplaintPerformanceMetricsCards } from '../../analytics/components';
import StatsCard from '../../../shared/components/common/StatsCard';
import { COMPLAINTS_DEFAULT_LOOKBACK_DAYS } from '../../complaints/hooks';
import TeamWorkloadTable from '../widgets/Tables/TeamWorkloadTable';
import FinancialGovernanceHelpModal from '../components/FinancialGovernanceHelpModal';
import { getActivityDescriptionParts } from './recentActivityHelpers';
import { ACTIVE_CRITICAL_COMPLAINTS_FILTER } from '../utils/complaintNavFilters';
import {
  DEVICES_BY_CONTRACT_COST_SORT,
  DEVICES_BY_PRODUCT_COST_SORT,
} from '../utils/deviceNavFilters';
import './Dashboards.css';

/**
 * ManagerDashboard Component
 *
 * Displays all 9 dashboard sections with location-scoped data visibility
 * (manager's assigned location only) and full financial transparency for budget planning
 * and oversight. Backend extracts location from authenticated user's JWT token.
 *
 * Props:
 * - token: JWT authentication token (optional, uses auth context if not provided)
 * - user: Current user object (optional, uses auth context if not provided)
 * - onNavigateToDevicesWithFilter: Handler to navigate to devices with filters
 * - onNavigateToComplaintsWithFilter: Handler to navigate to complaints with filters
 * - onNavigateToDeviceView: Handler to view device details
 * - onNavigateToAlerts: Handler to navigate to alerts
 * - onEditDeviceFromApprovals: Handler to edit a device from the approvals table (should set edit mode)
 *
 * Automatically uses authenticated user's role from auth context
 */
const ManagerDashboard = ({ token: providedToken, user: providedUser, onNavigateToDevicesWithFilter, onNavigateToComplaintsWithFilter, onNavigateToDeviceView, onNavigateToMaintenanceHistory, onNavigateToAlerts, onEditDeviceFromApprovals, onNavigateToActivityLog }) => {
  // Get authenticated user's role and token from context
  const { token: contextToken, user: contextUser } = useAuth();
  const token = providedToken || contextToken;
  const user = providedUser || contextUser;
  const [selectedComplaintForModal, setSelectedComplaintForModal] = useState(null);
  const [selectedMaintenanceForModal, setSelectedMaintenanceForModal] = useState(null);

  // Sorting state for Top Critical Complaints table
  const [complaintSortColumn, setComplaintSortColumn] = useState('created_at');
  const [complaintSortDirection, setComplaintSortDirection] = useState('desc');

  // Sorting state for Top 5 Highest Cost Devices table
  const [deviceSortColumn, setDeviceSortColumn] = useState('product_cost');
  const [deviceSortDirection, setDeviceSortDirection] = useState('desc');

  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [showFinancialSummaryHelp, setShowFinancialSummaryHelp] = useState(false);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Handle Top Critical Complaints sort
  const handleComplaintSort = (column) => {
    if (complaintSortColumn === column) {
      setComplaintSortDirection(complaintSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setComplaintSortColumn(column);
      setComplaintSortDirection('desc');
    }
  };

  // Sort complaints based on current sort settings
  const getSortedComplaints = (complaints) => {
    const sorted = [...complaints].sort((a, b) => {
      let aValue, bValue;

      if (complaintSortColumn === 'complaint_id') {
        aValue = a.complaint_identifier || '';
        bValue = b.complaint_identifier || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (complaintSortColumn === 'created_at') {
        // For date comparison, newer dates (higher values) come first in desc
        aValue = new Date(a.created_at || 0).getTime();
        bValue = new Date(b.created_at || 0).getTime();
      } else if (complaintSortColumn === 'assigned_to') {
        aValue = a.assigned_to || '';
        bValue = b.assigned_to || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (complaintSortColumn === 'status') {
        aValue = a.status || '';
        bValue = b.status || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }

      if (complaintSortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  };

  // Render sort indicator for complaints table
  const renderComplaintSortIndicator = (column) => {
    if (complaintSortColumn !== column) {
      return <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>⇅</span>;
    }
    return complaintSortDirection === 'desc'
      ? <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↓</span>
      : <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↑</span>;
  };

  // Handle Top 5 Highest Cost Devices sort
  const handleDeviceSort = (column) => {
    if (deviceSortColumn === column) {
      setDeviceSortDirection(deviceSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDeviceSortColumn(column);
      setDeviceSortDirection('desc');
    }
  };

  // Sort devices based on current sort settings
  const getSortedDevices = (devices) => {
    const sorted = [...devices].sort((a, b) => {
      let aValue, bValue;

      if (deviceSortColumn === 'device_id') {
        aValue = a.device_identifier || '';
        bValue = b.device_identifier || '';
        if (deviceSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (deviceSortColumn === 'department') {
        aValue = a.department || '';
        bValue = b.department || '';
        if (deviceSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (deviceSortColumn === 'product_cost') {
        aValue = parseFloat(a.product_cost || 0);
        bValue = parseFloat(b.product_cost || 0);
      } else if (deviceSortColumn === 'service_contract_cost') {
        aValue = parseFloat(a.service_contract_cost || 0);
        bValue = parseFloat(b.service_contract_cost || 0);
      } else if (deviceSortColumn === 'warranty_expiry') {
        aValue = new Date(a.warranty_expiry || 0).getTime();
        bValue = new Date(b.warranty_expiry || 0).getTime();
      }

      if (deviceSortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  };

  // Render sort indicator for devices table
  const renderDeviceSortIndicator = (column) => {
    if (deviceSortColumn !== column) {
      return <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>⇅</span>;
    }
    return deviceSortDirection === 'desc'
      ? <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↓</span>
      : <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↑</span>;
  };

  // Helper function to get current week date range (Monday to Sunday, ISO week)
  const getWeekDateRange = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

    // Calculate Monday of current week (go back to Monday if not already on Monday)
    const monday = new Date(today);
    const diff = monday.getDate() - monday.getDay() + (dayOfWeek === 0 ? -6 : 1); // Adjust when Sunday
    monday.setDate(diff);

    // Calculate Sunday of current week
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);

    const sameMonth = monday.getMonth() === sunday.getMonth();
    if (sameMonth) {
      const month = monday.toLocaleDateString('en-US', { month: 'short' });
      return `${month} ${monday.getDate()}-${sunday.getDate()}`;
    }
    const start = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${start}-${end}`;
  };

  const formatActivityTime = (timestamp) => {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const localIsoDateOffset = (days) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Create a handler for editing a device from approvals
  // This wraps onEditDeviceFromApprovals and handles fetching the device by ID
  const handleEditDeviceFromApprovals = async (deviceId) => {
    try {
      // Fetch the full device object by ID
      const response = await fetch(API_ENDPOINTS.DEVICES.GET_BY_ID(deviceId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch device');
      }

      const device = await response.json();

      // Call the callback from App.js which handles setting edit mode
      if (onEditDeviceFromApprovals) {
        onEditDeviceFromApprovals(device);
      }
    } catch (error) {
      console.error('Error fetching device for editing:', error);
      alert('Failed to load device for editing. Please try again.');
    }
  };

  // Create a handler for viewing a pending device from approvals (read-only mode)
  // Managers can view pending devices to see what was submitted for approval
  const handleViewDeviceFromApprovals = async (deviceId) => {
    try {
      // Fetch the full device object by ID
      const response = await fetch(API_ENDPOINTS.DEVICES.GET_BY_ID(deviceId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch device');
      }

      await response.json();

      // Call the callback from App.js which handles navigation to device view
      // The DeviceView component will automatically handle read-only mode based on device_status
      if (onNavigateToDeviceView) {
        onNavigateToDeviceView(deviceId);
      }
    } catch (error) {
      console.error('Error fetching device for viewing:', error);
      alert('Failed to load device for viewing. Please try again.');
    }
  };

  return (
    <BaseDashboard
      token={token}
      user={user}
    >
      {(apiResponse) => {
        // API returns { data: {...}, metadata: {...} }
        // Extract the actual dashboard data from the response
        const dashboardData = apiResponse?.data || {};

        // Extract all 9 dashboard sections from the data prop
        const statistics = dashboardData?.statistics_overview || {};
        const financial = dashboardData?.financial_summary || {};
        const finHighCostThreshold = Number(financial?.high_cost_approval_threshold ?? 0);
        const finHighCostThresholdLabel = finHighCostThreshold > 0
          ? formatCurrencyDynamic(finHighCostThreshold)
          : 'the configured threshold';
        const finAnnual = Number(financial?.annual_budget ?? 0);
        const finHasBudget = finAnnual > 0;
        const financeAlert = finHasBudget && !!financial?.budget_overrun;
        const financeUnbudgeted = !finHasBudget;
        const alerts = dashboardData?.alert_summary || {};
        const workload = dashboardData?.team_workload || {};
        const complaints = dashboardData?.complaints_overview || {};
        const maintenance = dashboardData?.maintenance_schedule || {};
        const devices = dashboardData?.device_status || {};
        const costs = dashboardData?.cost_analysis || {};
        const activity = dashboardData?.recent_activity || {};

        return (
          <DashboardLayout columns="1" gap="md">
            {/* Quick Navigation Links */}
            <div
              className={`admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
            >
              <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
                📍 Quick Navigation
              </div>
              <div className="admin-quick-nav-scroll">
                <div
                  className="admin-quick-nav-links"
                  style={{
                    display: 'flex',
                    flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                    gap: '0.5rem'
                  }}
                >
                  {[
                    { label: '📊 Statistics', id: 'statistics-overview' },
                    { label: '📋 Approvals', id: 'device-approvals' },
                    { label: '💰 Financial', id: 'financial-summary' },
                    { label: '🚨 Alerts', id: 'alert-summary' },
                    { label: '👥 Team', id: 'team-workload' },
                    { label: '📋 Complaints', id: 'complaints-overview' },
                    { label: '🔧 Maintenance', id: 'maintenance-schedule' },
                    { label: '🖥️ Devices', id: 'device-status' },
                    { label: '💸 Costs', id: 'cost-analysis' },
                    { label: '📊 Activity', id: 'recent-activity' }
                  ].map((link) => (
                    <a
                      key={link.id}
                      href={`#${link.id}`}
                      className="admin-quick-nav-link"
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: '#f3f4f6',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        color: 'var(--dp-accent-primary)',
                        textDecoration: 'none',
                        fontWeight: '500',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--dp-accent-primary)';
                        e.target.style.color = '#fff';
                        e.target.style.borderColor = 'var(--dp-accent-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f3f4f6';
                        e.target.style.color = 'var(--dp-accent-primary)';
                        e.target.style.borderColor = '#e5e7eb';
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* ========================================
                SECTION 1: STATISTICS OVERVIEW
                Quick health snapshot (8 key metrics) - All clickable for drill-down
                ======================================== */}
            <Section id="statistics-overview" title="📊 Statistics Overview">
              <div className="list-default-window-note" role="note">
                <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                <span>
                  Showing open tickets (any age) and closed/cancelled from the{' '}
                  <strong>last {COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days</strong>.
                  Same default as the Complaints list.
                </span>
              </div>
              <div className="dashboard-stats-summary">
                {[
                  { label: 'Total Devices', value: statistics.total_devices || 0, icon: '📱', className: 'total', action: 'view-all-devices' },
                  { label: 'Devices with Issues', value: statistics.devices_in_use || 0, icon: '⚠️', className: 'open', action: 'devices-with-issues' },
                  {
                    label: 'Total Complaints',
                    value: statistics.total_complaints || 0,
                    icon: '📝',
                    className: 'pending',
                    action: 'all-complaints',
                    // Matches Complaints default list (not all-time); details in tooltip
                    title: 'Open tickets (any age) + closed/cancelled in last 30 days. Click to open Complaints.',
                  },
                  { label: 'Open Complaints', value: statistics.complaints_open || 0, icon: '🔴', className: 'open', action: 'open-complaints' },
                  { label: 'In Progress', value: statistics.complaints_in_progress || 0, icon: '🟡', className: 'progress', action: 'in-progress-complaints' },
                  { label: 'Escalated to Vendor', value: statistics.complaints_escalated_to_vendor || 0, icon: '🚩', className: 'escalated', action: 'escalated-to-vendor-complaints' },
                  { label: 'Overdue Maintenance', value: statistics.devices_maintenance || 0, icon: '🔧', className: 'maintenance', action: 'overdue-maintenance', elevateWhenPositive: true },
                  { label: 'Critical Alerts', value: statistics.critical_alerts || 0, icon: '🚨', className: 'uncovered', action: 'critical-alerts', elevateWhenPositive: true }
                ].map((stat) => {
                  const handleStatClick = () => {
                    switch (stat.action) {
                      case 'view-all-devices':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({});
                        break;
                      case 'devices-with-issues':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          hasActiveComplaints: true
                        });
                        break;
                      case 'all-complaints':
                        // Empty filter → Complaints page server default (open any age + closed/cancelled 30d)
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({});
                        break;
                      case 'open-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({ statuses: ['open'] });
                        break;
                      case 'in-progress-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({ statuses: ['in_progress'] });
                        break;
                      case 'escalated-to-vendor-complaints':
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({ statuses: ['escalated_to_vendor'] });
                        break;
                      case 'overdue-maintenance':
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                          complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                        });
                        break;
                      case 'critical-alerts': {
                        const alertSection = document.getElementById('alert-summary');
                        if (alertSection) {
                          alertSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        break;
                      }
                      default:
                        break;
                    }
                  };

                  const elevate = stat.elevateWhenPositive && Number(stat.value) > 0;

                  return (
                    <StatsCard
                      key={stat.action}
                      icon={stat.icon}
                      number={stat.value}
                      label={stat.label}
                      subtext={stat.subtext}
                      className={`${stat.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                      onClick={handleStatClick}
                      title={stat.title || `Click to view ${stat.label.toLowerCase()}`}
                    />
                  );
                })}
              </div>
            </Section>

            {/* ========================================
                SECTION 1.5: DEVICE APPROVALS
                Manager's pending device approvals (Tabular format)
                ======================================== */}
            <Section id="device-approvals" title="📋 Device Approval Status">
              <ApprovalsTableWidget
                token={token}
                user={user}
                onEditDevice={handleEditDeviceFromApprovals}
                onViewDevice={handleViewDeviceFromApprovals}
              />
            </Section>

            {/* ========================================
                SECTION 2: FINANCIAL SUMMARY
                Budget and asset tracking (location-scoped, FULLY VISIBLE)
                Matches Admin Dashboard structure with Capex/Opex/Pending breakdown
                ======================================== */}
            <Section
              id="financial-summary"
              title={(
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: '1rem',
                  }}
                >
                  <span>💰 Financial Summary</span>
                  <button
                    type="button"
                    className="user-mgmt-help-btn"
                    onClick={() => setShowFinancialSummaryHelp(true)}
                    aria-label="Financial summary help"
                    title="What do these metrics mean?"
                    style={{ flexShrink: 0 }}
                  >
                    ?
                  </button>
                </span>
              )}
            >
              {financial && Object.keys(financial).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Location-scoped calendar-year YTD. <strong>Capex</strong> = approved devices;{' '}
                      <strong>Opex</strong> = pro-rated contracts. Pending = budget impact if approved.
                    </span>
                  </div>

                  <div className={`fg-budget-card ${financeAlert ? 'fg-budget-card--alert' : financeUnbudgeted ? 'fg-budget-card--unbudgeted' : 'fg-budget-card--ok'}`}>
                    <div className={`fg-budget-card__header ${financeAlert ? 'fg-budget-card__header--alert' : financeUnbudgeted ? 'fg-budget-card__header--unbudgeted' : 'fg-budget-card__header--ok'}`}>
                      {financeAlert ? '⚠️ Budget Alert' : financeUnbudgeted ? '📌 Calendar-year spend (annual budget not set)' : '✅ Budget On Track'}
                    </div>
                    {(() => {
                      const annualB = financial.annual_budget ?? 0;
                      const hasBudget = annualB > 0;
                      const pctOfBudget = (amount) => (
                        hasBudget ? formatPercentage((Number(amount) / annualB) * 100) : null
                      );
                      const utilizationPct = finHasBudget
                        ? financial.budget_utilization_percent
                        : null;

                      return (
                        <div className="fg-budget-card__metrics">
                          <div className="fg-budget-card__metric-cell">
                            <div className="fg-budget-card__metric-label">Annual Budget</div>
                            <div className="fg-budget-card__metric-value">
                              {formatCurrencyDynamic(financial.annual_budget)}
                            </div>
                            <div className="fg-budget-card__metric-subtext">Approved budget</div>
                          </div>
                          <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                          <div className="fg-budget-card__metric-cell">
                            <div className="fg-budget-card__metric-label">YTD Capex</div>
                            <div className={`fg-budget-card__metric-value fg-budget-card__metric-value--capex${financeAlert ? ' fg-budget-card__metric-value--capex-alert' : ''}`}>
                              {formatCurrencyDynamic(financial.ytd_capex)}
                              {financeAlert && (
                                <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>⚠️</span>
                              )}
                            </div>
                            <div className="fg-budget-card__metric-subtext">
                              {hasBudget
                                ? <>{pctOfBudget(financial.ytd_capex)} of budget</>
                                : <span className="fg-budget-card__metric-subtext--muted">Calendar-year approved devices</span>}
                            </div>
                          </div>
                          <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                          <div className="fg-budget-card__metric-cell">
                            <div className="fg-budget-card__metric-label">YTD Opex</div>
                            <div className="fg-budget-card__metric-value fg-budget-card__metric-value--opex">
                              {formatCurrencyDynamic(financial.ytd_opex)}
                            </div>
                            <div className="fg-budget-card__metric-subtext">
                              {hasBudget
                                ? <>{pctOfBudget(financial.ytd_opex)} of budget</>
                                : <span className="fg-budget-card__metric-subtext--muted">Pro-rated contracts</span>}
                            </div>
                          </div>
                          <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                          <div className="fg-budget-card__metric-cell">
                            <div className="fg-budget-card__metric-label">Pending Approvals (Location)</div>
                            <div className="fg-budget-card__metric-value fg-budget-card__metric-value--pending">
                              {formatCurrencyDynamic(financial.pending_cost)}
                            </div>
                            <div className="fg-budget-card__metric-subtext">
                              {hasBudget
                                ? <>{pctOfBudget(financial.pending_cost)} of budget</>
                                : <span className="fg-budget-card__metric-subtext--muted">If approved</span>}
                            </div>
                          </div>
                          <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                          <div className="fg-budget-card__metric-cell">
                            <div className="fg-budget-card__metric-label">Remaining</div>
                            <div className={`fg-budget-card__metric-value ${financial.budget_remaining > 0 ? 'fg-budget-card__metric-value--remaining-ok' : 'fg-budget-card__metric-value--remaining-bad'}`}>
                              {formatCurrencyDynamic(financial.budget_remaining)}
                            </div>
                            <div className="fg-budget-card__metric-subtext">
                              {hasBudget
                                ? <>{pctOfBudget(financial.budget_remaining)} of budget</>
                                : <span className="fg-budget-card__metric-subtext--muted">After YTD spend</span>}
                            </div>
                          </div>
                          <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                          <div className="fg-budget-card__metric-cell">
                            <div className="fg-budget-card__metric-label">Utilization</div>
                            {finHasBudget && utilizationPct != null ? (
                              <>
                                <div className={`fg-budget-card__metric-value fg-budget-card__metric-value--utilization${financial.budget_overrun ? ' fg-budget-card__metric-value--utilization-over' : ''}`}>
                                  {formatPercentage(utilizationPct)}
                                </div>
                                <div className="fg-budget-card__metric-subtext">YTD total utilization</div>
                              </>
                            ) : (
                              <>
                                <div className="fg-budget-card__metric-value" style={{ fontSize: '1rem' }}>
                                  Unbudgeted
                                </div>
                                <div className="fg-budget-card__metric-subtext fg-budget-card__metric-subtext--muted">
                                  Set location budget to track
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {financial.pending_cost > 0 && (
                      (() => {
                        const currentSpend = financial.ytd_capex + financial.ytd_opex;
                        const projectedSpend = currentSpend + financial.pending_cost;
                        const annualB = financial.annual_budget ?? 0;
                        const hasBudget = annualB > 0;
                        const isAlreadyOverBudget = hasBudget && currentSpend > annualB;
                        const willExceedBudget = hasBudget && projectedSpend > annualB;

                        return (
                          <div className={`fg-budget-card__impact${isAlreadyOverBudget ? ' fg-budget-card__impact--over' : ''}`}>
                            <div className="fg-budget-card__impact-head">
                              📋 <strong>Budget Impact</strong> - If all pending approvals are approved:
                            </div>
                            <div className="fg-budget-card__impact-row">
                              <span>
                                Total spend: {formatCurrencyDynamic(projectedSpend)}
                                {hasBudget
                                  ? <> ({formatPercentage((projectedSpend / annualB) * 100)} utilization)</>
                                  : <> (no annual budget allocated — utilization not calculated)</>}
                              </span>
                              <span style={{ fontWeight: '600', color: isAlreadyOverBudget ? '#d32f2f' : willExceedBudget ? '#d32f2f' : '#2e7d32' }}>
                                {isAlreadyOverBudget && willExceedBudget && '🔴 Already exceeded + further exceed'}
                                {isAlreadyOverBudget && !willExceedBudget && '⚠️ Already over budget'}
                                {!isAlreadyOverBudget && willExceedBudget && '⚠️ Would exceed'}
                                {!isAlreadyOverBudget && !willExceedBudget && '✅ Within budget'}
                              </span>
                            </div>
                            {isAlreadyOverBudget && (
                              <div className="fg-budget-card__impact-detail">
                                ⚠️ Current approved spend ({formatCurrencyDynamic(currentSpend)}) has already exceeded the annual budget ({formatCurrencyDynamic(annualB)}) by {formatCurrencyDynamic(currentSpend - annualB)}.
                                {willExceedBudget && (
                                  <div style={{ marginTop: '0.25rem' }}>
                                    Approving pending items will further increase overrun by {formatCurrencyDynamic(financial.pending_cost)}.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {/* 4 Financial Metrics Cards */}
                  <div className="financial-cards-grid responsive-grid responsive-grid-200" style={{ marginBottom: '1.5rem' }}>
                    {/* Total Capex Card */}
                    <div style={{
                      backgroundColor: '#fff',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      borderLeft: '4px solid #1976d2'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                        💰 Total Capex
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2', marginBottom: '0.5rem' }}>
                        {formatCurrencyDynamic(financial.total_capex)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                        Total device investment
                      </div>
                    </div>

                    {/* Total Maintenance Contracts Cost Card */}
                    <div style={{
                      backgroundColor: '#fff',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      borderLeft: '4px solid #4caf50'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                        📋 Total Maintenance Contracts
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50', marginBottom: '0.5rem' }}>
                        {formatCurrencyDynamic(financial.total_maintenance_contracts_cost)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                        LAMC + CAMC cost
                      </div>
                    </div>

                    {/* Annual Maintenance Contracts Card */}
                    <div style={{
                      backgroundColor: '#fff',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      borderLeft: '4px solid #f57c00'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                        💸 Annual Contract Cost
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00', marginBottom: '0.5rem' }}>
                        {formatCurrencyDynamic(financial.annual_contract_cost)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                        Yearly maintenance contract cost
                      </div>
                    </div>

                    {/* Contract to Value Ratio Card */}
                    <div style={{
                      backgroundColor: financial.contract_to_value_ratio > 15 ? '#f3e5f5' : '#fff',
                      padding: '1rem',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      borderLeft: '4px solid #9c27b0'
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                        📈 Contract to Value Ratio
                      </div>
                      <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: financial.contract_to_value_ratio > 15 ? '#c2185b' : '#9c27b0', marginBottom: '0.5rem' }}>
                        {formatPercentage(financial.contract_to_value_ratio)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                        Annual cost as % of total value
                      </div>
                      {financial.contract_to_value_ratio > 15 && (
                        <div style={{ fontSize: '0.65rem', color: '#c2185b', marginTop: '0.5rem', fontWeight: '500' }}>
                          ⚠️ High maintenance ratio
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="dashboard-subsection-label">Submissions & coverage</div>

                  <div className="fg-approvals-coverage-grid">
                    <div className="fg-high-cost-strip">
                      <div className="fg-high-cost-strip__header">
                        <div className="fg-high-cost-strip__title">⚠️ High-Cost Submissions</div>
                        <button
                          type="button"
                          className="fg-high-cost-strip__link"
                          onClick={() => scrollToSection('device-approvals')}
                        >
                          View in Approvals →
                        </button>
                      </div>
                      <div className="fg-high-cost-strip__metrics">
                        <div>
                          <div className="fg-high-cost-strip__metric-label">Pending &gt;{finHighCostThresholdLabel}</div>
                          <div className="fg-high-cost-strip__metric-value fg-high-cost-strip__metric-value--pending">
                            {financial.high_cost_pending || 0}
                          </div>
                        </div>
                        <div>
                          <div className="fg-high-cost-strip__metric-label">{financial.approval_rate_label}</div>
                          <div className="fg-high-cost-strip__metric-value fg-high-cost-strip__metric-value--rate">
                            {formatPercentage(financial.approval_rate || 0)}
                          </div>
                        </div>
                      </div>
                      {financial.high_cost_pending_list && financial.high_cost_pending_list.length > 0 ? (
                        <>
                          <div className="fg-high-cost-strip__devices">
                            {financial.high_cost_pending_list.slice(0, 4).map((device, idx) => (
                              <div key={idx} className="fg-high-cost-strip__device">
                                <div className="fg-high-cost-strip__device-name">
                                  {device.device_name || 'N/A'}
                                </div>
                                <div className="fg-high-cost-strip__device-meta">
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {device.department_icon} {device.department || 'N/A'}
                                  </span>
                                  <span className="fg-high-cost-strip__device-cost">{formatCurrencyDynamic(device.cost)}</span>
                                </div>
                                <div className="fg-high-cost-strip__device-days">
                                  {device.days_pending}d pending
                                </div>
                              </div>
                            ))}
                          </div>
                          {financial.high_cost_pending_list.length > 4 && (
                            <div className="fg-high-cost-strip__more">
                              +{financial.high_cost_pending_list.length - 4} more devices
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="fg-high-cost-strip__empty">No high-cost submissions pending approval.</p>
                      )}
                    </div>

                    <div className="fg-coverage-card">
                      <div className="fg-coverage-card__title">🔧 Asset Coverage Snapshot</div>
                      <div className="fg-coverage-card__metrics">
                        <div>
                          <div className="fg-coverage-card__metric-label">Coverage Expiring</div>
                          <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--expiring">
                            {financial.coverage_expiring || 0}
                          </div>
                        </div>
                        <div>
                          <div className="fg-coverage-card__metric-label">No Coverage (In-house)</div>
                          <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--nocov">
                            {financial.no_coverage || 0}
                          </div>
                        </div>
                        <div>
                          <div className="fg-coverage-card__metric-label">Coverage Expired</div>
                          <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--expired">
                            {financial.coverage_expired || 0}
                          </div>
                        </div>
                      </div>
                      <div className="fg-coverage-card__distribution">
                        <div className="fg-coverage-card__distribution-label">Service Distribution</div>
                        <div className="fg-coverage-card__distribution-stats">
                          {[
                            { icon: '🏥', label: 'Warranty', count: financial.warranty_count || 0 },
                            { icon: '🔧', label: 'LAMC', count: financial.lamc_count || 0 },
                            { icon: '⚙️', label: 'CAMC', count: financial.camc_count || 0 },
                            { icon: '🏢', label: 'In-house', count: financial.inhouse_count || 0 },
                          ].map((item, idx) => (
                            <Fragment key={item.label}>
                              {idx > 0 && <div className="fg-coverage-card__distribution-separator" aria-hidden="true" />}
                              <div className="fg-coverage-card__distribution-stat">
                                <div className="fg-coverage-card__distribution-stat-label">
                                  {item.icon} {item.label}
                                </div>
                                <div className="fg-coverage-card__distribution-stat-value">{item.count}</div>
                              </div>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <FinancialGovernanceHelpModal
                    isOpen={showFinancialSummaryHelp}
                    onClose={() => setShowFinancialSummaryHelp(false)}
                    highCostThresholdLabel={finHighCostThresholdLabel}
                    variant="manager"
                  />
                </>
              ) : (
                <EmptyState
                  section="Financial Summary"
                  message="No financial data available."
                  icon="📊"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 3: ALERT SUMMARY
                Critical items requiring attention
                ======================================== */}
            <Section id="alert-summary" title={`🚨 Alert Summary${alerts?.alerts && alerts.alerts.length > 0 ? ` (${alerts.total_alerts} alerts)` : ''}`}>
              {alerts?.alerts && alerts.alerts.length > 0 ? (
                <div className="maintenance-alerts-list">
                  {alerts.alerts.map((alert, idx) => {
                    const isClickable = alert.type === 'overdue_ppm' || alert.type === 'overdue_calibration' || alert.type === 'overdue_compliance'
                      || alert.type === 'critical_complaint' || alert.type === 'sla_violation_48h' || alert.type === 'sla_warning_24h';
                    const severityClass = alert.severity === 'critical' ? 'critical' : 'warning';

                    const handleAlertClick = () => {
                      if (!isClickable) return;

                      if (alert.type === 'overdue_ppm') {
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                        });
                      } else if (alert.type === 'overdue_calibration') {
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                        });
                      } else if (alert.type === 'overdue_compliance') {
                        onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                          complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)']
                        });
                      } else if (alert.type === 'critical_complaint') {
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter(
                          ACTIVE_CRITICAL_COMPLAINTS_FILTER
                        );
                      } else if (alert.type === 'sla_violation_48h') {
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                          daysOpenFrom: 2,
                          daysOpenTo: ''
                        });
                      } else if (alert.type === 'sla_warning_24h') {
                        onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                          statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                          daysOpenFrom: 1,
                          daysOpenTo: 2
                        });
                      }
                    };

                    return (
                      <div
                        key={idx}
                        className={`maintenance-alert-row ${severityClass}${isClickable ? ' clickable' : ''}`}
                        onClick={handleAlertClick}
                        title={isClickable ? (alert.action || 'Click to view') : undefined}
                      >
                        <div className="maintenance-alert-main">
                          <span>{alert.message}</span>
                          {alert.action ? (
                            <>
                              <span className="maintenance-alert-sep">·</span>
                              <span className="maintenance-alert-action">{alert.action}</span>
                            </>
                          ) : null}
                        </div>
                        <div className="maintenance-alert-meta">
                          <span className={`maintenance-alert-count ${severityClass}`}>
                            {alert.count}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  section="Alert Summary"
                  message="No critical alerts. All systems operating normally."
                  icon="🚨"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 4: TEAM WORKLOAD & PERFORMANCE
                Coverage vs fair share vs pace — not a hardcoded ticket cap
                ======================================== */}
            <Section id="team-workload" title="👥 Team Workload & Performance">
              {workload?.technicians && workload.technicians.length > 0 ? (
                <>
                  {workload.team_summary && (
                    <>
                      <div className="dashboard-stats-summary" style={{ marginBottom: '0.75rem' }}>
                        {[
                          { label: 'Open tickets', value: workload.team_summary.open_tickets || 0, icon: '🎫', className: 'total' },
                          {
                            label: 'Heavy queue',
                            value: workload.team_summary.above_share_count || 0,
                            icon: '📈',
                            className: 'uncovered',
                            elevate: (workload.team_summary.above_share_count || 0) > 0,
                          },
                          {
                            label: 'On-time (30d)',
                            value: formatPercentage(workload.team_summary.avg_completion_rate || 0),
                            icon: '✨',
                            className: 'closed',
                          },
                        ].map((metric) => (
                          <StatsCard
                            key={metric.label}
                            icon={metric.icon}
                            number={metric.value}
                            label={metric.label}
                            className={`${metric.className}${metric.elevate ? ' alert-elevated' : ''}`.trim()}
                          />
                        ))}
                      </div>
                      <div className="team-workload-signals" role="group" aria-label="Team workload checks">
                        {[
                          {
                            topic: 'Staffing',
                            finding: workload.team_summary.staffing_label || 'Keeping up',
                            evidence: `${workload.team_summary.created_30d || 0} new vs ${workload.team_summary.closed_30d || 0} closed (30d)`,
                            alert: workload.team_summary.staffing_status === 'understaffed',
                          },
                          {
                            topic: 'Device split',
                            finding: workload.team_summary.assignment_label || 'Even devices',
                            evidence: 'primary vs secondary',
                            alert: workload.team_summary.assignment_status === 'uneven',
                          },
                          {
                            topic: 'Auto-assign',
                            finding: workload.team_summary.routing_label || 'Even spread',
                            evidence: 'who got new vs who still holds',
                            alert: workload.team_summary.routing_status === 'biased',
                          },
                        ].map((signal) => (
                          <div
                            key={signal.topic}
                            className={`team-workload-signal${signal.alert ? ' is-alert' : ''}`}
                          >
                            <span className="team-workload-signal__topic">{signal.topic}</span>
                            <strong className="team-workload-signal__finding">{signal.finding}</strong>
                            <span className="team-workload-signal__evidence">{signal.evidence}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <TeamWorkloadTable technicians={workload.technicians} summary={workload.team_summary} />
                </>
              ) : (
                <EmptyState
                  section="Team Workload"
                  message="No technician data available."
                  icon="👥"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 5: COMPLAINTS OVERVIEW
                Complaint pipeline and status distribution
                ======================================== */}
            <Section id="complaints-overview" title="📋 Complaints Overview">
              {complaints && Object.keys(complaints).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Showing open tickets (any age) and closed/cancelled from the{' '}
                      <strong>last {COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days</strong>.
                      Same default as the Complaints list.
                    </span>
                  </div>
                  <div className="dashboard-stats-summary" style={{ marginBottom: '1.5rem' }}>
                    {[
                      {
                        label: 'Total Complaints',
                        value: complaints.total_complaints || 0,
                        icon: '📊',
                        className: 'total',
                        action: 'all-complaints',
                        title: 'Open tickets (any age) + closed/cancelled in last 30 days. Click to open Complaints.',
                      },
                      { label: 'Open', value: complaints.by_status?.open || 0, icon: '🔴', className: 'open', action: 'open' },
                      { label: 'In Progress', value: complaints.by_status?.in_progress || 0, icon: '🟡', className: 'progress', action: 'in_progress' },
                      { label: 'Escalated', value: complaints.by_status?.escalated_to_vendor || 0, icon: '🚩', className: 'escalated', action: 'escalated_to_vendor' },
                      {
                        label: 'Closed',
                        value: complaints.by_status?.closed || 0,
                        icon: '✅',
                        className: 'closed',
                        action: 'closed',
                        title: 'Closed in last 30 days (Complaints list default). Click to view.',
                      },
                      {
                        label: 'Cancelled',
                        value: complaints.by_status?.cancelled || 0,
                        icon: '❌',
                        className: 'cancelled',
                        action: 'cancelled',
                        title: 'Cancelled in last 30 days (Complaints list default). Click to view.',
                      },
                      { label: 'Pending >24h', value: complaints.pending_more_than_24h || 0, icon: '⏰', className: 'acknowledged', action: 'pending_24h', elevateWhenPositive: true },
                      { label: 'Pending >48h', value: complaints.pending_more_than_48h || 0, icon: '🚨', className: 'uncovered', action: 'pending_48h', elevateWhenPositive: true },
                      { label: 'Avg Resolution', value: `${(complaints.avg_resolution_hours || 0).toFixed(1)}h`, icon: '📈', className: 'pending' }
                    ].map((metric) => {
                      const handleMetricClick = () => {
                        if (!metric.action || !onNavigateToComplaintsWithFilter) return;

                        switch (metric.action) {
                          case 'all-complaints':
                            onNavigateToComplaintsWithFilter({});
                            break;
                          case 'open':
                            onNavigateToComplaintsWithFilter({ statuses: ['open'] });
                            break;
                          case 'in_progress':
                            onNavigateToComplaintsWithFilter({ statuses: ['in_progress'] });
                            break;
                          case 'escalated_to_vendor':
                            onNavigateToComplaintsWithFilter({ statuses: ['escalated_to_vendor'] });
                            break;
                          case 'closed':
                            onNavigateToComplaintsWithFilter({ statuses: ['closed'] });
                            break;
                          case 'cancelled':
                            onNavigateToComplaintsWithFilter({ statuses: ['cancelled'] });
                            break;
                          case 'pending_24h':
                            onNavigateToComplaintsWithFilter({
                              statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                              daysOpenFrom: 1,
                              daysOpenTo: ''
                            });
                            break;
                          case 'pending_48h':
                            onNavigateToComplaintsWithFilter({
                              statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                              daysOpenFrom: 2,
                              daysOpenTo: ''
                            });
                            break;
                          default:
                            break;
                        }
                      };

                      const elevate = metric.elevateWhenPositive && Number(metric.value) > 0;

                      return (
                        <StatsCard
                          key={metric.label}
                          icon={metric.icon}
                          number={metric.value}
                          label={metric.label}
                          className={`${metric.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                          onClick={metric.action ? handleMetricClick : undefined}
                          title={metric.title || (metric.action ? `Click to view ${metric.label.toLowerCase()}` : undefined)}
                        />
                      );
                    })}
                  </div>

                  <div className="dashboard-subsection">
                    <h3 className="dashboard-subsection-title">Complaint Metrics</h3>
                    <ComplaintPerformanceMetricsCards metrics={complaints} />
                  </div>

                  <div className="dashboard-subsection">
                    <h3 className="dashboard-subsection-title">Severity Breakdown</h3>
                    <div className="severity-breakdown-groups">
                      <div className="severity-breakdown-group">
                        <div className="dashboard-subsection-label">
                          Active ({(complaints.by_severity_active?.critical || 0) + (complaints.by_severity_active?.major || 0) + (complaints.by_severity_active?.minor || 0)})
                        </div>
                        <div className="dashboard-stats-summary">
                          {[
                            { severity: 'critical', label: 'Critical', className: 'uncovered', value: complaints.by_severity_active?.critical || 0, icon: '🔴' },
                            { severity: 'major', label: 'Major', className: 'acknowledged', value: complaints.by_severity_active?.major || 0, icon: '🟠' },
                            { severity: 'minor', label: 'Minor', className: 'total', value: complaints.by_severity_active?.minor || 0, icon: '🟢' }
                          ].map((item) => (
                            <StatsCard
                              key={`active-${item.severity}`}
                              icon={item.icon}
                              number={item.value}
                              label={item.label}
                              className={`${item.className}${Number(item.value) > 0 && item.severity === 'critical' ? ' alert-elevated' : ''}`}
                              onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                                statuses: ['open', 'acknowledged', 'in_progress', 'escalated_to_vendor'],
                                severities: [item.severity]
                              })}
                              title={`Click to view ${item.label.toLowerCase()} active complaints`}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="severity-breakdown-group">
                        <div className="dashboard-subsection-label">
                          Closed / Cancelled ({(complaints.by_severity_closed?.critical || 0) + (complaints.by_severity_closed?.major || 0) + (complaints.by_severity_closed?.minor || 0)})
                        </div>
                        <div className="dashboard-stats-summary">
                          {[
                            { severity: 'critical', label: 'Critical', className: 'uncovered', value: complaints.by_severity_closed?.critical || 0, icon: '🔴' },
                            { severity: 'major', label: 'Major', className: 'acknowledged', value: complaints.by_severity_closed?.major || 0, icon: '🟠' },
                            { severity: 'minor', label: 'Minor', className: 'total', value: complaints.by_severity_closed?.minor || 0, icon: '🟢' }
                          ].map((item) => (
                            <StatsCard
                              key={`closed-${item.severity}`}
                              icon={item.icon}
                              number={item.value}
                              label={item.label}
                              className={item.className}
                              onClick={() => onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({
                                statuses: ['closed', 'cancelled'],
                                severities: [item.severity]
                              })}
                              title={`Click to view ${item.label.toLowerCase()} closed/cancelled (last ${COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days)`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>  
              ) : (
                <EmptyState
                  section="Complaints Overview"
                  message="No complaint data available."
                  icon="📋"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 5.5: TOP CRITICAL COMPLAINTS
                Detailed list of active critical complaints
                ======================================== */}
            <Section
              id="critical-complaints"
              title={(
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: '1rem',
                  }}
                >
                  <span>
                    {`🚨 Top Critical Complaints${dashboardData?.critical_complaints && dashboardData.critical_complaints.length > 0
                      ? ` (${dashboardData.critical_complaints.length})` : ''}`}
                  </span>
                  {onNavigateToComplaintsWithFilter ? (
                    <button
                      type="button"
                      className="btn-view-all-devices"
                      onClick={() => onNavigateToComplaintsWithFilter(ACTIVE_CRITICAL_COMPLAINTS_FILTER)}
                      title="View all active critical complaints in Complaints"
                      style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      View all critical in Complaints ›
                    </button>
                  ) : null}
                </span>
              )}
            >
              {dashboardData?.critical_complaints && dashboardData.critical_complaints.length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Top 10 open critical tickets: <strong>unassigned</strong> first, then in progress, then oldest (SLA risk).
                    </span>
                  </div>
                  <div className="critical-complaints-table-container">
                  <table className="critical-complaints-table">
                    <thead>
                      <tr>
                        <th className="col-rank">#</th>
                        <th
                          className="col-complaint"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleComplaintSort('complaint_id')}
                        >
                          Complaint {renderComplaintSortIndicator('complaint_id')}
                        </th>
                        <th className="col-device">Device</th>
                        <th className="col-department">Department</th>
                        <th
                          className="col-reported"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleComplaintSort('created_at')}
                        >
                          Reported {renderComplaintSortIndicator('created_at')}
                        </th>
                        <th
                          className="col-assigned"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleComplaintSort('assigned_to')}
                        >
                          Assigned To {renderComplaintSortIndicator('assigned_to')}
                        </th>
                        <th
                          className="col-status"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleComplaintSort('status')}
                        >
                          Status {renderComplaintSortIndicator('status')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedComplaints(dashboardData.critical_complaints.slice(0, 10)).map((complaint, index) => {
                        // Helper function to format days since
                        const getDaysSince = (dateString) => {
                          if (!dateString) return 'N/A';
                          const date = new Date(dateString);
                          const today = new Date();
                          const days = Math.floor((today - date) / (1000 * 60 * 60 * 24));
                          if (days === 0) return 'Today';
                          if (days === 1) return '1 day ago';
                          return `${days} days ago`;
                        };

                        return (
                          <tr
                            key={index}
                            className="complaint-row clickable"
                            onClick={() => {
                               setSelectedComplaintForModal(complaint);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className="col-rank">
                              <span className="rank-badge">{index + 1}</span>
                            </td>
                            <td className="col-complaint complaint-stacked-cell">
                              <div className="stacked-content">
                                <span className="complaint-title">{complaint.title}</span>
                                <ComplaintIDBadge complaintId={complaint.complaint_identifier || 'N/A'} size="small" />
                              </div>
                            </td>
                            <td className="col-device device-stacked-cell">
                              <div className="stacked-content">
                                <span className="device-name">{complaint.device_name}</span>
                                <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                              </div>
                            </td>
                            <td className="col-department">
                              <span className="department-cell">
                                <span className="department-icon-small">{complaint.department_icon || '🏢'}</span>
                                <span className="department-text">{complaint.department || 'N/A'}</span>
                              </span>
                            </td>
                            <td className="col-reported">
                              <span className="reported-text">{getDaysSince(complaint.created_at)}</span>
                            </td>
                            <td className="col-assigned">
                              <span className="assigned-text">{complaint.assigned_to || '—'}</span>
                            </td>
                            <td className="col-status center">
                              <StatusBadge status={complaint.status || 'unknown'} size="small" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
              ) : (
                <EmptyState
                  section="Critical Complaints"
                  message="No critical complaints at this time."
                  icon="🚨"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 6: MAINTENANCE SCHEDULE
                Device maintenance compliance and schedule
                ======================================== */}
            <Section id="maintenance-schedule" title="🔧 Maintenance Schedule & Compliance">
              {maintenance && Object.keys(maintenance).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Current schedule snapshot. Compliance rate colors: <strong>Green ≥80%</strong>, Amber 60–79%, Red &lt;60%.
                      Click a type card to view overdue devices.
                    </span>
                  </div>
                  <div className="maintenance-type-grid">
                    {[
                      {
                        key: 'ppm',
                        label: 'PPM',
                        rate: maintenance.compliance_rates?.ppm_rate,
                        filter: { ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'] },
                      },
                      {
                        key: 'calibration',
                        label: 'Calibration',
                        rate: maintenance.compliance_rates?.calibration_rate,
                        filter: { calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'] },
                      },
                      {
                        key: 'compliance',
                        label: 'Compliance',
                        rate: maintenance.compliance_rates?.regulatory_rate,
                        filter: { complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'] },
                      },
                    ].map((type) => {
                      const counts = maintenance.by_type?.[type.key] || {};
                      const isNA = typeof type.rate === 'object' && type.rate?.value === null;
                      const percentage = isNA ? null : (type.rate || 0);
                      let rateClass = 'na';
                      if (percentage !== null) {
                        rateClass = percentage < 60 ? 'bad' : percentage < 80 ? 'warn' : 'ok';
                      }

                      return (
                        <div
                          key={type.key}
                          className="maintenance-type-card"
                          onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter(type.filter)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter(type.filter);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          title={`Click to view overdue ${type.label.toLowerCase()}`}
                        >
                          <div className="maintenance-type-card__header">
                            <span className="maintenance-type-card__title">{type.label}</span>
                            {isNA ? (
                              <span className="maintenance-type-card__rate na" title={type.rate?.reason || 'No data available'}>N/A</span>
                            ) : (
                              <span className={`maintenance-type-card__rate ${rateClass}`}>
                                {formatPercentage(percentage)}
                              </span>
                            )}
                          </div>
                          <div className="maintenance-type-card__metrics">
                            <div className="maintenance-type-metric">
                              <div className="maintenance-type-metric__label">Overdue</div>
                              <div className="maintenance-type-metric__value overdue">{counts.overdue || 0}</div>
                            </div>
                            <div className="maintenance-type-metric">
                              <div className="maintenance-type-metric__label">Due Soon</div>
                              <div className="maintenance-type-metric__value due-soon">{counts.due_soon || 0}</div>
                            </div>
                            <div className="maintenance-type-metric">
                              <div className="maintenance-type-metric__label">Current</div>
                              <div className="maintenance-type-metric__value current">{counts.current || 0}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="dashboard-stats-summary">
                    <StatsCard
                      icon="📅"
                      number={maintenance.due_this_week || 0}
                      label="Due This Week"
                      subtext={getWeekDateRange()}
                      className="calibration"
                      title="Current week, Monday to Sunday"
                    />
                    <StatsCard
                      icon="📆"
                      number={maintenance.due_this_month || 0}
                      label="Due This Month"
                      subtext={new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      className="pending"
                    />
                    <StatsCard
                      icon="⚠️"
                      number={formatCurrencyDynamic(maintenance.overdue_maintenance_value || 0)}
                      label="Overdue Value"
                      subtext="Prepaid at-risk work"
                      className={`uncovered${(maintenance.overdue_maintenance_value || 0) > 0 ? ' alert-elevated' : ''}`}
                    />
                  </div>
                </>
              ) : (
                <EmptyState
                  section="Maintenance Schedule"
                  message="No maintenance schedule data available."
                  icon="🔧"
                />
              )}

              <div className="dashboard-subsection maintenance-due-calendar">
                <CalendarView
                  variant="compact"
                  token={token}
                  onNavigateToDevices={() => {
                    if (onNavigateToDevicesWithFilter) {
                      onNavigateToDevicesWithFilter({});
                    }
                  }}
                  onViewDevice={(deviceId) => {
                    if (onNavigateToDeviceView) {
                      onNavigateToDeviceView(deviceId);
                    }
                  }}
                />
              </div>
            </Section>

            {/* ========================================
                SECTION 7: DEVICE STATUS
                Portfolio health and device distribution
                ======================================== */}
            <Section id="device-status" title="🖥️ Device Portfolio Status">
              {devices && Object.keys(devices).length > 0 ? (
                <>
                  <div className="list-default-window-note" role="note">
                    <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                    <span>
                      Current inventory snapshot. Coverage cards include already expired plus due within 30 days.
                      Click a card to open the matching Devices list.
                    </span>
                  </div>
                  <div className="dashboard-stats-summary" style={{ marginBottom: '1.25rem' }}>
                    {[
                      {
                        label: 'Active',
                        value: devices.total_active || 0,
                        icon: '✅',
                        className: 'active',
                        filter: { deviceStatus: ['active'] },
                        title: 'Click to view active devices',
                      },
                      {
                        label: 'In Maintenance',
                        value: devices.in_maintenance || 0,
                        icon: '🔧',
                        className: 'open',
                        filter: { hasActiveComplaints: true },
                        title: 'Devices with an open complaint. Click to view.',
                      },
                      {
                        label: 'Retired',
                        value: devices.retired || 0,
                        icon: '🗑️',
                        className: 'inactive',
                        title: 'Retired devices are not shown on the Devices list.',
                      },
                      {
                        label: 'Warranty Expiring',
                        value: devices.warranty_expiring || 0,
                        icon: '⏰',
                        className: 'uncovered',
                        elevateWhenPositive: true,
                        filter: { warrantyDateTo: localIsoDateOffset(30) },
                        title: 'Warranty expired or expiring within 30 days. Click to view.',
                      },
                      {
                        label: 'Contract Expiring',
                        value: devices.contract_expiring || 0,
                        icon: '📋',
                        className: 'expiring',
                        elevateWhenPositive: true,
                        filter: { contractDateTo: localIsoDateOffset(30) },
                        title: 'Contract expired or expiring within 30 days. Click to view.',
                      },
                      {
                        label: 'Avg Age (years)',
                        value: (devices.avg_device_age_years || 0).toFixed(1),
                        icon: '📈',
                        className: 'total',
                        title: 'Typical age from installation or purchase date.',
                      },
                      {
                        label: '>5 Years Old',
                        value: devices.devices_over_5_years || 0,
                        icon: '⚠️',
                        className: 'expiring',
                        elevateWhenPositive: true,
                        title: 'Older than 5 years from installation or purchase date.',
                      },
                    ].map((metric) => {
                      const elevate = metric.elevateWhenPositive && Number(metric.value) > 0;
                      return (
                        <StatsCard
                          key={metric.label}
                          icon={metric.icon}
                          number={metric.value}
                          label={metric.label}
                          className={`${metric.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                          onClick={metric.filter && onNavigateToDevicesWithFilter
                            ? () => onNavigateToDevicesWithFilter(metric.filter)
                            : undefined}
                          title={metric.title}
                        />
                      );
                    })}
                  </div>
                  {devices.health_status && (
                    <div className="dashboard-subsection">
                      <div className="dashboard-subsection-title">Health Status</div>
                      <div className="dashboard-stats-summary">
                        {[
                          {
                            label: 'Healthy',
                            value: devices.health_status.healthy || 0,
                            icon: '💚',
                            className: 'active',
                            title: 'No open critical or major complaints (snapshot).',
                          },
                          {
                            label: 'At Risk',
                            value: devices.health_status.at_risk || 0,
                            icon: '⚠️',
                            className: 'expiring',
                            title: 'Has an open major complaint (snapshot).',
                          },
                          {
                            label: 'Critical',
                            value: devices.health_status.critical || 0,
                            icon: '🚨',
                            className: 'uncovered',
                            elevateWhenPositive: true,
                            title: 'Has an open critical complaint (snapshot).',
                          },
                        ].map((health) => {
                          const elevate = health.elevateWhenPositive && Number(health.value) > 0;
                          return (
                            <StatsCard
                              key={health.label}
                              icon={health.icon}
                              number={health.value}
                              label={health.label}
                              className={`${health.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                              title={health.title}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <EmptyState
                  section="Device Status"
                  message="No device status data available."
                  icon="🖥️"
                />
              )}
            </Section>

            {/* ========================================
                SECTION 8: COST ANALYSIS
                Budget planning and financial breakdown
                ======================================== */}
            <Section id="cost-analysis" title="💸 Cost Analysis & Budget">
              {costs && Object.keys(costs).length > 0 ? (
                <>
                  {costs.ytd_maintenance_vs_budget && (
                    <div className="dashboard-subsection">
                      <div className="dashboard-subsection-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span>YTD Maintenance Spend vs Annual Commitment</span>
                        {costs.ytd_maintenance_vs_budget.utilization_status && (
                          <span className={`cost-util-pill cost-util-pill--${costs.ytd_maintenance_vs_budget.utilization_status}`}>
                            {costs.ytd_maintenance_vs_budget.utilization_status === 'green' ? 'On track' :
                              costs.ytd_maintenance_vs_budget.utilization_status === 'amber' ? 'Watch' : 'At risk'}
                          </span>
                        )}
                      </div>
                      {costs.ytd_maintenance_vs_budget.status_message && (
                        <p className="dashboard-subsection-copy">{costs.ytd_maintenance_vs_budget.status_message}</p>
                      )}

                      <div className="list-default-window-note" role="note">
                        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                        <span>
                          YTD spend is actual PPM / Calibration / Compliance logs this year on LAMC/CAMC devices.
                          Annual commitment is use-it-or-lose-it contract cost.
                          Utilization: <strong>Green 70-100%</strong>, Amber 40-69%, Red &lt;40%.
                          {(costs.ytd_maintenance_vs_budget.devices_with_contracts !== undefined || costs.ytd_maintenance_vs_budget.total_devices !== undefined) && (
                            <>
                              {' '}
                              <button
                                type="button"
                                className="dashboard-inline-link"
                                onClick={() => onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                                  serviceContractTypes: ['LAMC', 'CAMC']
                                })}
                                title="Click to view devices with LAMC/CAMC contracts"
                              >
                                {costs.ytd_maintenance_vs_budget.devices_with_contracts || 0} of {costs.ytd_maintenance_vs_budget.total_devices || 0} active devices
                              </button>
                              {' '}have contributing LAMC/CAMC contracts.
                            </>
                          )}
                        </span>
                      </div>

                      <div className="financial-cards-grid responsive-grid responsive-grid-200">
                        {(() => {
                          const utilStatus = costs.ytd_maintenance_vs_budget.utilization_status;
                          const utilColors = utilStatus === 'green'
                            ? { border: '#4caf50', value: '#2e7d32', background: '#fff' }
                            : utilStatus === 'amber'
                              ? { border: '#f59e0b', value: '#92400e', background: '#fffbeb' }
                              : { border: '#dc2626', value: '#991b1b', background: '#fef2f2' };
                          return (
                            <>
                              <div style={{
                                backgroundColor: '#fff',
                                padding: '1rem',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                borderLeft: '4px solid #1976d2'
                              }}>
                                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                                  💸 YTD Spend
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2', marginBottom: '0.5rem' }}>
                                  {formatCurrencyDynamic(costs.ytd_maintenance_vs_budget.ytd_spend || 0)}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                                  Actual logged work
                                </div>
                              </div>
                              <div style={{
                                backgroundColor: '#fff',
                                padding: '1rem',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                borderLeft: '4px solid #f57c00'
                              }}>
                                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                                  📅 Expected YTD Spend
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00', marginBottom: '0.5rem' }}>
                                  {formatCurrencyDynamic(costs.ytd_maintenance_vs_budget.expected_ytd_spend || 0)}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                                  {costs.ytd_maintenance_vs_budget.year_progress_percent || 0}% through year
                                </div>
                              </div>
                              <div style={{
                                backgroundColor: '#fff',
                                padding: '1rem',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                borderLeft: '4px solid #4caf50'
                              }}>
                                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                                  📑 Annual Commitment
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50', marginBottom: '0.5rem' }}>
                                  {formatCurrencyDynamic(costs.ytd_maintenance_vs_budget.annual_commitment || 0)}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                                  Full-year contract cost
                                </div>
                              </div>
                              <div style={{
                                backgroundColor: utilColors.background,
                                padding: '1rem',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                borderLeft: `4px solid ${utilColors.border}`
                              }}>
                                <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                                  📊 Expected Utilization
                                </div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: utilColors.value, marginBottom: '0.5rem' }}>
                                  {formatPercentage(costs.ytd_maintenance_vs_budget.expected_utilization_percent || 0)}
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                                  vs scheduled
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {costs.highest_cost_devices && costs.highest_cost_devices.length > 0 && (
                    <div className="highest-cost-devices-section">
                      <div className="highest-cost-devices-header">
                        <h3 className="dashboard-subsection-title">Top 5 Highest Cost Devices</h3>
                        {onNavigateToDevicesWithFilter ? (
                          <div className="highest-cost-devices-links">
                            <button
                              type="button"
                              className="btn-view-all-devices"
                              onClick={() => onNavigateToDevicesWithFilter(DEVICES_BY_PRODUCT_COST_SORT)}
                              title="Open Devices sorted by highest purchase cost"
                            >
                              View all by device cost ›
                            </button>
                            <button
                              type="button"
                              className="btn-view-all-devices"
                              onClick={() => onNavigateToDevicesWithFilter(DEVICES_BY_CONTRACT_COST_SORT)}
                              title="Open Devices sorted by highest annual contract cost"
                            >
                              View by contract cost ›
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <div className="highest-cost-devices-table-container">
                        <table className="highest-cost-devices-table">
                          <thead>
                            <tr>
                              <th className="col-rank">#</th>
                              <th
                                className="col-device"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDeviceSort('device_id')}
                              >
                                Device {renderDeviceSortIndicator('device_id')}
                              </th>
                              <th
                                className="col-department"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDeviceSort('department')}
                              >
                                Department {renderDeviceSortIndicator('department')}
                              </th>
                              <th
                                className="col-device-cost"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDeviceSort('product_cost')}
                              >
                                Device Cost {renderDeviceSortIndicator('product_cost')}
                              </th>
                              <th
                                className="col-contract-cost"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDeviceSort('service_contract_cost')}
                              >
                                Annual Contract Cost {renderDeviceSortIndicator('service_contract_cost')}
                              </th>
                              <th
                                className="col-warranty-status"
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleDeviceSort('warranty_expiry')}
                              >
                                Warranty Status {renderDeviceSortIndicator('warranty_expiry')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {getSortedDevices(costs.highest_cost_devices.slice(0, 5)).map((device, index) => {
                              // Helper function to determine warranty status
                              const getWarrantyStatus = (warrantyExpiry) => {
                                if (!warrantyExpiry) return { status: 'N/A', badgeClass: 'status-na', daysLeft: null, showHelpText: false };

                                try {
                                  // Parse the warranty expiry date (handle both ISO format and other formats)
                                  const expiryDate = new Date(warrantyExpiry);

                                  // Validate the date
                                  if (isNaN(expiryDate.getTime())) {
                                    return { status: 'Invalid', badgeClass: 'status-na', daysLeft: null, showHelpText: false };
                                  }

                                  // Get today's date at midnight (UTC) for accurate comparison
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);

                                  // Set expiry date to midnight for fair comparison
                                  const expiryDateAtMidnight = new Date(expiryDate);
                                  expiryDateAtMidnight.setHours(0, 0, 0, 0);

                                  // Calculate days left
                                  const daysLeft = Math.floor((expiryDateAtMidnight - today) / (1000 * 60 * 60 * 24));

                                  if (daysLeft < 0) {
                                    // Warranty has expired
                                    return { status: '✗ Expired', badgeClass: 'status-expired', daysLeft, showHelpText: false };
                                  } else if (daysLeft <= 30) {
                                    // Active but expiring soon - show help text with days left
                                    return { status: '✓ Active', badgeClass: 'status-active', daysLeft, showHelpText: true };
                                  } else {
                                    // Active with plenty of time left - no help text needed
                                    return { status: '✓ Active', badgeClass: 'status-active', daysLeft: null, showHelpText: false };
                                  }
                                } catch (error) {
                                  console.error('Error calculating warranty status:', error);
                                  return { status: 'Error', badgeClass: 'status-na', daysLeft: null, showHelpText: false };
                                }
                              };

                              const warranty = getWarrantyStatus(device.warranty_expiry);

                              return (
                                <tr key={index} className="device-row">
                                  <td className="col-rank">
                                    <span className="rank-badge">{index + 1}</span>
                                  </td>
                                  <td className="col-device device-stacked-cell">
                                    <div className="stacked-content">
                                      <span className="device-name">{device.name || 'N/A'}</span>
                                      <DeviceIDBadge deviceId={device.device_identifier || 'N/A'} size="small" />
                                    </div>
                                  </td>
                                  <td className="col-department">
                                    <span className="department-cell">
                                      <span className="department-icon-small">{device.department_icon || '🏢'}</span>
                                      <span className="department-text">{device.department || 'N/A'}</span>
                                    </span>
                                  </td>
                                  <td className="col-device-cost">
                                    <strong>{formatCurrencyDynamic(parseFloat(device.product_cost || 0))}</strong>
                                  </td>
                                  <td className="col-contract-cost">
                                    <strong>{formatCurrencyDynamic(parseFloat(device.service_contract_cost || 0))}</strong>
                                  </td>
                                  <td className="col-warranty-status">
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                                      <span className={`warranty-status-badge ${warranty.badgeClass}`}>
                                        {warranty.status}
                                      </span>
                                      {warranty.showHelpText && warranty.daysLeft !== null && (
                                        <span className="warranty-help-text">
                                          expiring in {warranty.daysLeft} {warranty.daysLeft === 1 ? 'day' : 'days'}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                 </>
                ) : (
                  <EmptyState
                    section="Cost Analysis"
                    message="No cost analysis data available."
                    icon="💸"
                  />
              )}
            </Section>

            {/* ========================================
                SECTION 9: RECENT ACTIVITY
                System activity log (last 7 days)
                ======================================== */}
            <Section id="recent-activity" title={`📊 Recent Activity${((activity?.activities && activity.activities.length > 0) || (activity?.total_activities || 0) > 0) ? ` (Last 7 Days)` : ''}`}>
              {((activity?.activities && activity.activities.length > 0) || (activity?.total_activities || 0) > 0) ? (
                (() => {
                  const today = new Date();
                  const sevenDaysAgo = new Date(today);
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const dateRange = `${formatDate(sevenDaysAgo)} - ${formatDate(today)}`;
                  const activityRows = activity?.activities || [];
                  const summary = activity?.summary || {};
                  const stats = {
                    complaints_opened: summary.complaints_opened ?? 0,
                    complaints_closed: summary.complaints_closed ?? 0,
                    complaints_cancelled: summary.complaints_cancelled ?? 0,
                    complaints_escalated: summary.complaints_escalated ?? 0,
                    maintenance_by_type: {
                      ppm: summary.maintenance_by_type?.ppm ?? 0,
                      calibration: summary.maintenance_by_type?.calibration ?? 0,
                      compliance: summary.maintenance_by_type?.compliance ?? 0,
                    },
                  };
                  const totalInWindow = activity?.total_activities ?? summary.total ?? activityRows.length;
                  const PREVIEW_LIMIT = 10;
                  const visibleRows = activityRows.slice(0, PREVIEW_LIMIT);

                  return (
                    <div>
                      <div className="list-default-window-note" role="note">
                        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                        <span>
                          Card counts are the full 7 days from this location's activity log, <strong>{dateRange}</strong>.
                          The list shows up to the 10 newest.
                        </span>
                      </div>
                      <div className="dashboard-stats-summary" style={{ marginBottom: '1rem' }}>
                        <StatsCard
                          icon="📋"
                          number={stats.complaints_opened}
                          label="Complaints Opened"
                          className="open"
                        />
                        <StatsCard
                          icon="✅"
                          number={stats.complaints_closed}
                          label="Complaints Closed"
                          className="closed"
                        />
                        <StatsCard
                          icon="❌"
                          number={stats.complaints_cancelled}
                          label="Complaints Cancelled"
                          className="cancelled"
                        />
                        <StatsCard
                          icon="🚩"
                          number={stats.complaints_escalated}
                          label="Escalated"
                          className="escalated"
                          title="Moved to vendor in this 7-day window"
                        />
                        <StatsCard
                          icon="📅"
                          number={stats.maintenance_by_type.ppm}
                          label="PPM"
                          className="pending"
                        />
                        <StatsCard
                          icon="⚙️"
                          number={stats.maintenance_by_type.calibration}
                          label="Calibration"
                          className="calibration"
                        />
                        <StatsCard
                          icon="✅"
                          number={stats.maintenance_by_type.compliance}
                          label="Compliance"
                          className="active"
                        />
                      </div>

                      <h3 className="dashboard-activity-list-heading">Most recent</h3>
                      <div className="dashboard-activity-list">
                        {visibleRows.map((act, idx) => {
                          const getMaintenanceTypeLabel = (maintenanceType) => {
                            const labelMap = {
                              'ppm': 'PPM',
                              'calibration': 'CAL',
                              'compliance': 'COM'
                            };
                            return labelMap[maintenanceType?.toLowerCase()] || maintenanceType;
                          };

                          const isComplaint = act.activity_type === 'complaint_created'
                            || act.activity_type === 'complaint_closed'
                            || act.activity_type === 'complaint_cancelled'
                            || act.activity_type === 'complaint_escalated';
                          const isMaintenance = act.activity_type === 'maintenance_logged';
                          const { label, rest } = getActivityDescriptionParts(act);
                          const deviceIdentifier = act.device_identifier
                            || (isComplaint && typeof act.device_id === 'string' ? act.device_id : null)
                            || (isComplaint ? null : act.entity_id);
                          const numericDeviceId = act.device_row_id
                            ?? (typeof act.device_id === 'number' ? act.device_id : null);
                          const canOpenComplaint = Boolean(act.complaint_id);
                          const canOpenDevice = Boolean(numericDeviceId) && typeof onNavigateToDeviceView === 'function';
                          const canOpenMaintenance = isMaintenance && (
                            Boolean(act.maintenance_log_id)
                            || (Boolean(deviceIdentifier) && typeof onNavigateToMaintenanceHistory === 'function')
                          );
                          const rowOpensComplaint = isComplaint && canOpenComplaint;
                          const rowOpensMaintenance = isMaintenance && canOpenMaintenance;
                          const rowClickable = rowOpensComplaint || rowOpensMaintenance;

                          const openComplaint = () => {
                            if (canOpenComplaint) setSelectedComplaintForModal({ id: act.complaint_id });
                          };
                          const openDevice = () => {
                            if (canOpenDevice) onNavigateToDeviceView(numericDeviceId);
                          };
                          const openMaintenance = () => {
                            if (act.maintenance_log_id) {
                              setSelectedMaintenanceForModal({ id: act.maintenance_log_id });
                            } else if (canOpenMaintenance && typeof onNavigateToMaintenanceHistory === 'function') {
                              onNavigateToMaintenanceHistory({ device_identifier: deviceIdentifier });
                            }
                          };
                          const handleRowClick = () => {
                            if (rowOpensComplaint) openComplaint();
                            else if (rowOpensMaintenance) openMaintenance();
                          };

                          return (
                            <div
                              key={`${act.activity_type}-${act.entity_id}-${act.timestamp}-${idx}`}
                              className={`dashboard-activity-row${isComplaint ? ' is-complaint' : isMaintenance ? ' is-maintenance' : ''}${rowClickable ? ' is-clickable' : ''}`}
                              onClick={rowClickable ? handleRowClick : undefined}
                              title={rowOpensComplaint ? 'View complaint details' : rowOpensMaintenance ? 'View maintenance details' : undefined}
                            >
                              <div className="dashboard-activity-row__main">
                                {isMaintenance && act.maintenance_type && (
                                  <span
                                    className="maintenance-type-badge"
                                    data-type={act.maintenance_type?.toLowerCase()}
                                  >
                                    {getMaintenanceTypeLabel(act.maintenance_type)}
                                  </span>
                                )}
                                {isComplaint && act.entity_id && (
                                  <ComplaintIDBadge
                                    complaintId={act.entity_id}
                                    size="small"
                                    clickable={canOpenComplaint}
                                    title={canOpenComplaint ? 'View complaint details' : undefined}
                                    onClick={canOpenComplaint ? openComplaint : undefined}
                                  />
                                )}
                                {deviceIdentifier && (
                                  <DeviceIDBadge
                                    deviceId={deviceIdentifier}
                                    size="small"
                                    clickable={canOpenDevice}
                                    title={canOpenDevice ? 'View device details' : undefined}
                                    onClick={canOpenDevice ? openDevice : undefined}
                                  />
                                )}
                                <span className="dashboard-activity-row__desc">
                                  {label ? (
                                    <>
                                      <strong className="dashboard-activity-row__action">{label}</strong>
                                      {rest ? `: ${rest}` : ''}
                                    </>
                                  ) : (
                                    rest
                                  )}
                                </span>
                              </div>
                              <div className="dashboard-activity-row__meta">
                                <span title={act.actor_employee_id || undefined}>{act.actor_name || 'Unknown'}</span>
                                <span>•</span>
                                <span>{formatActivityTime(act.timestamp)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {totalInWindow > visibleRows.length && (
                        <div className="dashboard-activity-list-footer">
                          <span>
                            {visibleRows.length} of {totalInWindow} in this 7-day window
                          </span>
                          {typeof onNavigateToActivityLog === 'function' && (
                            <button
                              type="button"
                              className="link-button dashboard-activity-view-all"
                              onClick={() => onNavigateToActivityLog()}
                            >
                              View activity log
                            </button>
                          )}
                        </div>
                      )}
                      {totalInWindow <= visibleRows.length && totalInWindow > 0 && typeof onNavigateToActivityLog === 'function' && (
                        <div className="dashboard-activity-list-footer">
                          <button
                            type="button"
                            className="link-button dashboard-activity-view-all"
                            onClick={() => onNavigateToActivityLog()}
                          >
                            View activity log
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <EmptyState
                  section="Recent Activity"
                  message="No device management activities in the last 7 days."
                  icon="📊"
                />
              )}
            </Section>

            {/* ========================================
                COMPLAINT VIEW MODAL
                Displays complaint details when a row is clicked
                ======================================== */}
            {selectedComplaintForModal && (
              <ComplaintView
                complaintId={selectedComplaintForModal.id}
                token={token}
                onClose={() => setSelectedComplaintForModal(null)}
                onUpdate={() => {
                  // For manager dashboard, refresh might not be necessary as they see aggregated data
                  // But we can add it for consistency if needed
                }}
                isFullPage={false}
                user={user}
              />
            )}

            {selectedMaintenanceForModal && (
              <MaintenanceLogView
                maintenanceLogId={selectedMaintenanceForModal.id}
                token={token}
                onClose={() => setSelectedMaintenanceForModal(null)}
                onNavigateToDeviceView={onNavigateToDeviceView}
                onNavigateToMaintenanceHistory={onNavigateToMaintenanceHistory}
              />
            )}
          </DashboardLayout>
        );
      }}
    </BaseDashboard>
  );
};

export default ManagerDashboard;
